import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { mockData } from '../mock';
import { companyApi, analyticsApi } from '../hooks/useApi';

export const ContactSection = () => {
  const [companyInfo, setCompanyInfo] = useState(mockData.contact);

  useEffect(() => {
    // Load real company info from backend
    const loadCompanyInfo = async () => {
      try {
        const info = await companyApi.getInfo();
        setCompanyInfo(info);
      } catch (error) {
        console.error('Error loading company info:', error);
        // Fallback to mock data
      }
    };
    loadCompanyInfo();
  }, []);

  const handleWhatsApp = async () => {
    try {
      // Track WhatsApp click
      await analyticsApi.trackClick('whatsapp');
    } catch (error) {
      console.error('Error tracking WhatsApp click:', error);
    }
    const phoneNumber = companyInfo.whatsapp;
    const message = encodeURIComponent('Hola, me gustaría información sobre sus servicios de reparación');
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      const link = document.createElement('a');
      link.href = whatsappUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePhoneCall = async () => {
    try {
      // Track phone click
      await analyticsApi.trackClick('phone');
    } catch (error) {
      console.error('Error tracking phone click:', error);
    }
    window.open(`tel:${companyInfo.phone.replace(/\s/g, '')}`, '_self');
  };

  return (
    <section id="contacto" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-6">
            Contacto
          </h2>
          <p className="text-2xl md:text-3xl font-bold text-[#f97316] mb-4">
            ERBA: tu maquinaria agrícola y de jardín lista siempre
          </p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Contacta con nosotros para una atención personalizada y rápida
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="border-none shadow-xl bg-white">
              <CardHeader>
                <CardTitle className="text-2xl text-[#004534] flex items-center">
                  <Phone className="h-6 w-6 mr-3 text-[#16a34a]" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <button
                  onClick={handlePhoneCall}
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                >
                  <Phone className="h-5 w-5 text-[#16a34a] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#004534]">Teléfono</h4>
                    <p className="text-gray-600">{companyInfo.phone}</p>
                  </div>
                </button>

                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Mail className="h-5 w-5 text-[#16a34a] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#004534]">Email</h4>
                    <p className="text-gray-600">{companyInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-[#16a34a] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#004534]">Dirección</h4>
                    <p className="text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-[#16a34a] mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#004534]">Horarios</h4>
                    <p className="text-gray-600">{companyInfo.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp CTA removed as requested */}
          </div>

          {/* Additional WhatsApp Contact Card */}
          <Card className="border-none shadow-xl bg-gradient-to-br from-[#16a34a] to-[#f97316] text-white">
            <CardContent className="p-8 text-center">
              <MessageCircle className="h-16 w-16 mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4">¡Contacta Ahora!</h3>
              <p className="text-green-100 mb-6 text-lg">
                Para cualquier consulta sobre reparaciones, mantenimiento o repuestos, 
                contáctanos directamente por WhatsApp
              </p>
              <Button 
                onClick={handleWhatsApp}
                className="bg-white text-[#16a34a] hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-xl transition-all transform hover:scale-105 shadow-lg"
              >
                <MessageCircle className="h-6 w-6 mr-3" />
                Abrir WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};